// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useState } from 'react'
import { analytics } from '../../../services/analytics'

interface UseCanvasModesParams {
    selectedNodeIds: string[]
    sessionId: string | null
    frameEditState?: Record<string, { layers: Array<{ id: string }> }>
}

interface CanvasModesAPI {
    frameModeById: Record<string, boolean>
    brushModeById: Record<string, boolean>
    handleFrameModeToggle: (id: string) => void
    handleBrushModeToggle: (id: string) => void
    resetModes: () => void
}

/**
 * Manages per-item mode state for frame editing and drawing overlays.
 * 
 * - Frame mode: Controls whether an item shows the crop/frame editing overlay
 * - Brush mode: Controls whether an item shows the drawing overlay
 * 
 * Modes are automatically cleaned up when:
 * - Items are deselected (unless frame has multiple images)
 * - Session changes
 */
export function useCanvasModes(params: UseCanvasModesParams): CanvasModesAPI {
    const { selectedNodeIds, sessionId, frameEditState } = params

    const [frameModeById, setFrameModeById] = useState<Record<string, boolean>>({})
    const [brushModeById, setBrushModeById] = useState<Record<string, boolean>>({})

    // Helper: Remove modes for items that are no longer selected
    // Exception: Keep frame mode if frame has multiple images
    const cleanupModeMap = useCallback(
        (prevMap: Record<string, boolean>, isFrameMode: boolean): Record<string, boolean> => {
            const next = { ...prevMap }
            let changed = false
            for (const id of Object.keys(prevMap)) {
                if (!selectedNodeIds.includes(id)) {
                    const layerCount = frameEditState?.[id]?.layers?.length ?? 0
                    if (isFrameMode && layerCount > 1) {
                        continue
                    }
                    delete next[id]
                    changed = true
                }
            }
            return changed ? next : prevMap
        },
        [selectedNodeIds, frameEditState]
    )

    // Toggle handlers
    const handleFrameModeToggle = useCallback((id: string) => {
        setFrameModeById((prev) => ({ ...prev, [id]: !prev[id] }))
    }, [])

    const handleBrushModeToggle = useCallback((id: string) => {
        setBrushModeById((prev) => {
            const nextEnabled = !prev[id]

            // Track brush mode toggle
            analytics.track('brush_mode_toggled', {
                enabled: nextEnabled,
                session_id: sessionId,
                node_id: id,
            })

            return { ...prev, [id]: nextEnabled }
        })
    }, [sessionId])

    // Reset all modes
    const resetModes = useCallback(() => {
        setFrameModeById({})
        setBrushModeById({})
    }, [])

    // Cleanup modes when selection changes
    useEffect(() => {
        setFrameModeById((prev) => cleanupModeMap(prev, true))
        setBrushModeById((prev) => cleanupModeMap(prev, false))
    }, [cleanupModeMap])

    // Reset modes when session changes
    useEffect(() => {
        resetModes()
    }, [sessionId, resetModes])

    return {
        frameModeById,
        brushModeById,
        handleFrameModeToggle,
        handleBrushModeToggle,
        resetModes,
    }
}

